/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */
import React from "react";
import Accordion from 'react-bootstrap/Accordion';
import { useTranslation } from "react-i18next";
import Card from 'react-bootstrap/Card';

import SearchComponent from "../../../../components/SearchMC";
import CheckBoxMC from "../../../../components/CheckBoxMC";
import IconMC from "../../../../components/IconMC";
import ButtonMC from "../../../../components/ButtonMC";
import ProductListHeader from "../../ProductListHeader";
import ProductPriceDisplay from "./ProductPriceDisplay";

import { formatMoney } from "../../../../../util/numberUtils";
import { canBeSelected, hasDiscount } from "../../../../../util/productUtils";
import { useSearchProducts } from "../../hooks/useSearchProducts";
import usePaginatedScrollUI from "../../../../hooks/usePaginatedScrollUI";

const SearchProductsForm = ({
  selectedProducts,
  onCancel,
  addProducts,
  helpText
}) => {
  const { t: tCommon } = useTranslation('Common');
  const { t } = useTranslation('Orders');
  const { t: tProducts } = useTranslation('Products');

  const {
    products,
    pagination,
    currency_symbol,
    currency_code,
    searchText,
    searchedProductExists,
    isSearching,
    setSearchText,
    searchProducts,
    handleSelectProduct,
    handleSelectCombination,
    addFakeProduct,
    handleAddProducts
  } = useSearchProducts({
    selectedProducts,
    addProducts
  });

  const { containerRef, handleScroll } = usePaginatedScrollUI({
    loading: isSearching,
    pagination,
    onNextPage: searchProducts,
    threshold: 100,
    direction: 'down'
  });

  return (
    <div className="tw-wrapper">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="tw-flex tw-flex-col tw-gap-4 tw-max-h-[calc(100vh-200px)] tw-overflow-y-auto tw-pb-[100px]"
      >
        {helpText && (
          <p className="tw-text-xs tw-text-gray-500">{helpText}</p>
        )}

        <SearchComponent
          onChange={(value) => setSearchText(value)}
          placeholder={t('search.searchBy')}
          defaultValue=""
          debounceTime={500}
        />

        <Accordion bsPrefix="list-accordion" alwaysOpen>
          {(products || []).map((product, index) => (
            <Card key={`product-list-${product.id}`} bsPrefix="list-card">
              <Card.Header bsPrefix="list-header">
                <ProductListHeader eventKey={`${index}`} product={product} handleSelectProduct={handleSelectProduct} />
              </Card.Header>
              <Accordion.Collapse eventKey={`${index}`}>
                <Card.Body bsPrefix="list-card-body">
                  {product.product_variant_combinations.map((combination) => (
                    <div key={`product-combination-${product.id}-${combination.id}`} className="tw-pl-10 tw-py-2.5 tw-border-b tw-border-gray-200">
                      <div className="tw-flex tw-flex tw-justify-between">
                        <div className="tw-flex tw-pl-6">
                          {canBeSelected(combination) ? (
                            <div className="tw-flex">
                              <CheckBoxMC
                                checked={combination.wasSelected || combination.selected || false}
                                onChange={
                                  combination.wasSelected
                                    ? () => { }
                                    : (e) => handleSelectCombination(e, product, combination)
                                }
                                className={`tw-w-3 tw-h-3 ${combination.wasSelected ? '!tw-text-gray-300 !tw-accent-gray-300' : ''}`}
                              />
                            </div>
                          ) : (
                            <div className="tw-w-4" />
                          )}

                          <div className="tw-flex tw-flex-col tw-pl-6">
                            <span className={`${combination.wasSelected ? 'tw-text-gray-300' : 'tw-text-gray-700'}`}>{combination.name}</span>
                            {combination.wasSelected && (
                              <div className="tw-flex tw-ml-[5px]">
                                <div className="tw-text-gray-500">
                                  <span className="tw-mr-[5px]">
                                    <IconMC
                                      name="info-outline"
                                      className="tw-text-gray-500"
                                      size="base"
                                    />
                                  </span>
                                  <span>{t('search.addedProduct')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="md:tw-col-span-3 tw-text-right px-0">
                          <span className={`${combination.wasSelected ? 'tw-text-gray-300' : 'tw-text-gray-700'} ${Number(combination.quantity) <= 0 ? 'tw-text-red-500' : ''}`}>
                            {combination.quantity}
                            {' '}
                            {t('search.inStock')}
                          </span>
                        </div>
                        <ProductPriceDisplay
                          item={combination}
                          currency_symbol={currency_symbol}
                          currency_code={currency_code}
                          t={t}
                        />
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          ))}
        </Accordion>

        {isSearching && (
          <div className="tw-flex tw-justify-center tw-py-4">
            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-blue-500" />
          </div>
        )}

        {!searchedProductExists && searchText && !isSearching && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-text-center tw-flex-col tw-p-6 tw-gap-2">
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-gray-950 tw-text-sm">{t('search.notProducts')}</span>
                <span className="tw-text-gray-950 tw-text-sm">{t('search.canCreate')}</span>
              </div>
              <div className="tw-flex tw-justify-center tw-items-center">
                <ButtonMC
                  text={t('search.createProduct')}
                  onClick={addFakeProduct}
                  className="tw-px-6 tw-py-1"
                  variant="SECONDARY"
                  outlined
                />
              </div>
            </div>

            <div className="tw-flex tw-p-4 tw-bg-yellow-50 tw-rounded-lg tw-gap-2">
              <div>
                <IconMC name="info-outline" className="tw-text-yellow-600" size="sm" />
              </div>
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-yellow-600 tw-text-xs">{t('search.description')}</span>
                <span className="tw-text-yellow-600 tw-text-xs">
                  {t('search.toMake')}
                  {t('search.path')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="tw-bg-white tw-absolute tw-bottom-0 tw-right-0 tw-h-[85px] tw-w-full tw-bg-white tw-flex tw-flex-col tw-px-7 tw-py-4 tw-gap-2">
        <ButtonMC
          text={tCommon('actions.add')}
          onClick={handleAddProducts}
          className="tw-w-full"
        />
        <div className="tw-font-medium tw-text-blue-500 tw-text-sm tw-text-center tw-cursor-pointer" onClick={onCancel}>{tCommon('actions.cancel')}</div>
      </div>
    </div>
  );
};

export default SearchProductsForm;
